<template>
  <div class="w-full bg-white font-['Poppins'] min-h-screen">
    <ToastAlert
      :is-active="toastAlert"
      :type="typeMsg"
      :message="msg"
      :title="tittle"
      @close="closeToast"
    />

    <!-- LOADER -->
    <div v-if="loader_profile" class="fixed inset-0 flex items-center justify-center bg-white z-50">
      <img
        src="@/assets/imgs/tuin_loader_v2.gif"
        alt="cargando..."
        class="w-full max-w-[500px] px-4"
      >
    </div>

    <!-- ═══════════════════════════════════════════════════════════════
         MODO: Tiene suscripción activa con tarjeta → UI de cambio de plan
         ═══════════════════════════════════════════════════════════════ -->
    <div v-if="hasActiveCardSubscription && !loader_profile">
      <div class="w-full px-4 sm:px-6 lg:px-20 py-6 lg:py-10">

        <!-- Header -->
        <div class="flex flex-col items-center justify-center mb-8 lg:mb-10">
          <div class="w-full max-w-[720px] text-center mb-2">
            <h1 class="text-stone-950 text-xl sm:text-2xl font-medium leading-7">
              Tu plan actual
            </h1>
            <p class="text-neutral-500 text-sm sm:text-base mt-2">
              Selecciona un plan diferente para actualizarlo al siguiente ciclo de facturación.
            </p>
          </div>

          <!-- Toggle Mensual/Anual -->
          <div class="flex items-center gap-2 mt-4">
            <span class="text-stone-950 text-sm sm:text-base font-medium">Mensual</span>
            <button
              @click="togglePeriod"
              class="relative w-10 h-5 bg-gray-200 rounded-full transition-colors duration-200"
              :class="{ 'bg-rose-500': isAnual }"
            >
              <div
                class="absolute w-4 h-4 bg-white rounded-full top-[2px] transition-transform duration-200"
                :class="isAnual ? 'translate-x-[22px]' : 'translate-x-[2.5px]'"
              ></div>
            </button>
            <div class="flex items-center gap-1.5">
              <span class="text-neutral-500 text-sm sm:text-base font-medium">Anual</span>
              <span class="bg-rose-600 text-white text-[10px] sm:text-xs font-semibold px-2 py-0.5 rounded-md shadow-sm animate-bounce">
                -20%
              </span>
            </div>
          </div>
        </div>

        <div v-if="planChangeBlocked" class="w-full max-w-[860px] mx-auto mt-8 mb-6">
          <div class="flex gap-3 items-start p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <svg class="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            </svg>
            <div>
              <p class="text-sm font-semibold text-amber-800">Cambio de plan no disponible</p>
              <p class="text-xs text-amber-700 mt-0.5 leading-relaxed">
                Ya realizaste un cambio de plan este mes. Podrás seleccionar un nuevo plan
                a partir de tu próximo ciclo de facturación.
              </p>
            </div>
          </div>
        </div>
        
        <!-- Grid de planes (modo informativo + seleccionable) -->
        <div class="w-full max-w-[860px] mx-auto">
          <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 gap-6 justify-items-center">

            <div
              v-for="plan in displayPlans"
              :key="plan.key"
              @click="selectPlan(plan)"
              :class="[
                'w-full max-w-[384px] p-6 sm:p-8 bg-white rounded-lg shadow-md border-2 flex flex-col cursor-pointer transition-all duration-200',
                selectedPlanKey === plan.key
                  ? 'border-rose-500 shadow-rose-100'
                  : isCurrentPlan(plan)
                    ? 'border-gray-300 opacity-80 cursor-default'
                    : 'border-gray-200 hover:border-rose-300 hover:shadow-lg'
              ]"
            >
              <!-- Badge plan actual -->
              <div class="flex justify-between items-start mb-4">
                <h3 class="text-gray-500 text-lg sm:text-xl font-medium">{{ plan.label }}</h3>
                <span
                  v-if="isCurrentPlan(plan)"
                  class="flex-shrink-0 bg-rose-100 text-rose-700 text-xs font-semibold px-2 py-1 rounded-full"
                >
                  Plan actual
                </span>
                <!-- Checkmark si está seleccionado -->
                <div
                  v-else-if="selectedPlanKey === plan.key"
                  class="flex-shrink-0 w-6 h-6 bg-rose-500 rounded-full flex items-center justify-center"
                >
                  <svg class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
              </div>

              <!-- Precio -->
              <div class="flex items-end gap-2 mb-6">
                <span class="text-gray-900 text-4xl sm:text-5xl font-extrabold leading-[48px]">
                  ${{ plan.price }}
                </span>
                <span class="text-gray-500 text-base sm:text-lg font-medium">
                  MXN/{{ isAnual ? 'Anual' : 'Mensual' }}
                </span>
              </div>

              <!-- Features -->
              <div class="flex flex-col gap-3 flex-1">
                <div class="flex items-center gap-3">
                  <div class="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <div class="w-3.5 h-3.5 bg-rose-500 rounded-sm"></div>
                  </div>
                  <span class="text-gray-500 text-sm sm:text-base">{{ plan.properties }}</span>
                </div>
                <div class="flex items-center gap-3">
                  <div class="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <div class="w-3.5 h-3.5 bg-rose-500 rounded-sm"></div>
                  </div>
                  <span class="text-gray-500 text-sm sm:text-base">Exposición automática en 6 portales</span>
                </div>
              </div>

              <!-- Indicador de selección (solo planes que no son el actual) -->
              <div v-if="!isCurrentPlan(plan)" class="mt-5 pt-4 border-t border-gray-100">
                <span
                  v-if="selectedPlanKey === plan.key"
                  class="text-rose-500 text-sm font-semibold"
                >
                  ✓ Seleccionado
                </span>
                <span v-else class="text-gray-400 text-sm">
                  Clic para seleccionar
                </span>
              </div>
            </div>

          </div>
        </div>

        <!-- Panel de confirmación (aparece cuando se selecciona un plan distinto al actual) -->
        <transition
          enter-active-class="transition ease-out duration-200"
          enter-from-class="opacity-0 translate-y-4"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition ease-in duration-150"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-4"
        >
          <div
            v-if="selectedPlanKey && !isCurrentPlanSelected"
            class="w-full max-w-[860px] mx-auto mt-8"
          >
            <div class="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">

              <!-- Resumen del cambio -->
              <div class="flex flex-col sm:flex-row sm:items-center gap-4 mb-5">
                <div class="flex-1">
                  <p class="text-sm text-gray-500 mb-1">Plan actual</p>
                  <p class="text-base font-semibold text-gray-800">
                    {{ currentSubscription.plan_name }} — ${{ currentSubscription.plan_price }} MXN/{{ currentSubscription.billing_cycle }}
                  </p>
                </div>
                <div class="hidden sm:flex items-center text-gray-400">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <p class="text-sm text-gray-500 mb-1">Nuevo plan</p>
                  <p class="text-base font-semibold text-rose-600">
                    {{ selectedPlanObj?.label }} — ${{ selectedPlanObj?.price }} MXN/{{ isAnual ? 'anual' : 'mensual' }}
                  </p>
                </div>
              </div>

              <!-- Nota informativa -->
              <div class="flex gap-2 items-start p-3 bg-gray-50 rounded-lg border border-gray-100 mb-5">
                <svg class="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <p class="text-xs text-gray-500 leading-relaxed">
                  El cambio de plan se aplicará en tu <strong>próximo ciclo de facturación</strong>.
                  Hasta entonces, seguirás disfrutando de tu plan actual.
                </p>
              </div>

              <!-- Botones -->
              <div class="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  @click="selectedPlanKey = null"
                  class="px-5 py-2.5 border border-gray-200 rounded-lg text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  @click="confirmPlanChange"
                  :disabled="loadingChange"
                  class="px-5 py-2.5 bg-rose-500 rounded-lg text-white text-sm font-medium hover:bg-rose-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 justify-center"
                >
                  <svg
                    v-if="loadingChange"
                    class="animate-spin w-4 h-4"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                  </svg>
                  {{ loadingChange ? 'Actualizando...' : 'Confirmar cambio de plan' }}
                </button>
              </div>
            </div>
          </div>
        </transition>

      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════
         MODO ORIGINAL: Sin suscripción activa con tarjeta → cards con link a Stripe
         ═══════════════════════════════════════════════════════════════ -->
    <div v-else-if="!loader_profile">
      <div v-show="!showModal">
        <div class="w-full px-4 sm:px-6 lg:px-20 py-6 lg:py-10">

          <!-- Header de selección de plan -->
          <div class="flex flex-col items-center justify-center mb-8 lg:mb-12">
            <div class="w-full max-w-[720px] text-center mb-4">
              <h1 class="text-stone-950 text-xl sm:text-2xl font-medium leading-7">
                Selecciona tu plan
              </h1>
            </div>

            <!-- Toggle Mensual/Anual -->
            <div class="flex items-center gap-2">
              <span class="text-stone-950 text-sm sm:text-base font-medium">Mensual</span>
              <button
                @click="togglePeriod"
                class="relative w-10 h-5 bg-gray-200 rounded-full transition-colors duration-200"
                :class="{ 'bg-rose-500': isAnual }"
              >
                <div
                  class="absolute w-4 h-4 bg-white rounded-full top-[2px] transition-transform duration-200"
                  :class="isAnual ? 'translate-x-[22px]' : 'translate-x-[2.5px]'"
                ></div>
              </button>
              <div class="flex items-center gap-1.5">
                <span class="text-neutral-500 text-sm sm:text-base font-medium">Anual</span>
                <span class="bg-rose-600 text-white text-[10px] sm:text-xs font-semibold px-2 py-0.5 rounded-md shadow-sm animate-bounce">
                  -20%
                </span>
              </div>
            </div>
          </div>

          <!-- Grid de planes (modo original con botones de pago) -->
          <div class="w-full max-w-[800px] mx-auto">
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-2 gap-10 justify-items-center">

              <!-- Plan 3 propiedades -->
              <div class="w-full max-w-[384px] p-6 sm:p-8 bg-white rounded-lg shadow-md border border-gray-200 flex flex-col">
                <div class="flex-1 flex flex-col gap-7">
                  <div class="flex flex-col gap-4">
                    <h3 class="text-gray-500 text-lg sm:text-xl font-medium">3 propiedades</h3>
                    <div class="flex items-end gap-2.5">
                      <span class="text-gray-900 text-4xl sm:text-5xl font-extrabold leading-[48px]">
                        ${{ isAnual ? '1,430' : '149' }}
                      </span>
                      <span class="text-gray-500 text-base sm:text-lg font-medium">
                        MXN/{{ isAnual ? 'Anual' : 'Mensual' }}
                      </span>
                    </div>
                  </div>
                  <div class="flex flex-col gap-4">
                    <div class="flex items-center gap-3">
                      <div class="w-5 h-5 flex items-center justify-center">
                        <div class="w-3.5 h-3.5 bg-rose-500 rounded-sm"></div>
                      </div>
                      <span class="text-gray-500 text-sm sm:text-base">3 propiedades</span>
                    </div>
                    <div class="flex items-center gap-3">
                      <div class="w-5 h-5 flex items-center justify-center">
                        <div class="w-3.5 h-3.5 bg-rose-500 rounded-sm"></div>
                      </div>
                      <span class="text-gray-500 text-sm sm:text-base">Exposición automática en 6 portales</span>
                    </div>
                  </div>
                  <fwb-button
                    @click="() => goToStripe('plan3')"
                    :disabled="!isActiveLocation"
                    class="w-full flex justify-center items-center px-5 py-2.5 bg-rose-500 hover:bg-rose-600 rounded-lg transition-colors duration-200 cursor-pointer"
                  >
                    <span class="text-white text-sm font-medium">Contratar Plan</span>
                  </fwb-button>
                </div>
              </div>

              <!-- Plan 5 propiedades -->
              <div class="w-full max-w-[384px] p-6 sm:p-8 bg-white rounded-lg shadow-md border border-gray-200 flex flex-col">
                <div class="flex-1 flex flex-col gap-7">
                  <div class="flex flex-col gap-4">
                    <h3 class="text-gray-500 text-lg sm:text-xl font-medium">5 propiedades</h3>
                    <div class="flex items-end gap-2.5">
                      <span class="text-gray-900 text-4xl sm:text-5xl font-extrabold leading-[48px]">
                        ${{ isAnual ? '1,910' : '199' }}
                      </span>
                      <span class="text-gray-500 text-base sm:text-lg font-medium">
                        MXN/{{ isAnual ? 'Anual' : 'Mensual' }}
                      </span>
                    </div>
                  </div>
                  <div class="flex flex-col gap-4">
                    <div class="flex items-center gap-3">
                      <div class="w-5 h-5 flex items-center justify-center">
                        <div class="w-3.5 h-3.5 bg-rose-500 rounded-sm"></div>
                      </div>
                      <span class="text-gray-500 text-sm sm:text-base">5 propiedades</span>
                    </div>
                    <div class="flex items-center gap-3">
                      <div class="w-5 h-5 flex items-center justify-center">
                        <div class="w-3.5 h-3.5 bg-rose-500 rounded-sm"></div>
                      </div>
                      <span class="text-gray-500 text-sm sm:text-base">Exposición automática en 6 portales</span>
                    </div>
                  </div>
                  <fwb-button
                    @click="() => goToStripe('plan5')"
                    :disabled="!isActiveLocation"
                    class="w-full flex justify-center items-center px-5 py-2.5 bg-rose-500 hover:bg-rose-600 rounded-lg transition-colors duration-200 cursor-pointer"
                  >
                    <span class="text-white text-sm font-medium">Contratar Plan</span>
                  </fwb-button>
                </div>
              </div>

              <!-- Plan 10 propiedades -->
              <div class="w-full max-w-[384px] p-6 sm:p-8 bg-white rounded-lg shadow-md border border-gray-200 flex flex-col">
                <div class="flex-1 flex flex-col gap-7">
                  <div class="flex flex-col gap-4">
                    <h3 class="text-gray-500 text-lg sm:text-xl font-medium">10 propiedades</h3>
                    <div class="flex items-end gap-2.5">
                      <span class="text-gray-900 text-4xl sm:text-5xl font-extrabold leading-[48px]">
                        ${{ isAnual ? '3,830' : '399' }}
                      </span>
                      <span class="text-gray-500 text-base sm:text-lg font-medium">
                        MXN/{{ isAnual ? 'Anual' : 'Mensual' }}
                      </span>
                    </div>
                  </div>
                  <div class="flex flex-col gap-4">
                    <div class="flex items-center gap-3">
                      <div class="w-5 h-5 flex items-center justify-center">
                        <div class="w-3.5 h-3.5 bg-rose-500 rounded-sm"></div>
                      </div>
                      <span class="text-gray-500 text-sm sm:text-base">10 propiedades</span>
                    </div>
                    <div class="flex items-center gap-3">
                      <div class="w-5 h-5 flex items-center justify-center">
                        <div class="w-3.5 h-3.5 bg-rose-500 rounded-sm"></div>
                      </div>
                      <span class="text-gray-500 text-sm sm:text-base">Exposición automática en 6 portales</span>
                    </div>
                  </div>
                  <fwb-button
                    @click="() => goToStripe('plan10')"
                    :disabled="!isActiveLocation"
                    class="w-full flex justify-center items-center px-5 py-2.5 bg-rose-500 hover:bg-rose-600 rounded-lg transition-colors duration-200 cursor-pointer"
                  >
                    <span class="text-white text-sm font-medium">Contratar Plan</span>
                  </fwb-button>
                </div>
              </div>

              <!-- Plan Ilimitado -->
              <div class="w-full max-w-[384px] p-6 sm:p-8 bg-white rounded-lg shadow-md border border-gray-200 flex flex-col">
                <div class="flex-1 flex flex-col gap-7">
                  <div class="flex flex-col gap-4">
                    <h3 class="text-gray-500 text-lg sm:text-xl font-medium">Ilimitado</h3>
                    <div class="flex items-end gap-2.5">
                      <span class="text-gray-900 text-4xl sm:text-5xl font-extrabold leading-[48px]">
                        ${{ isAnual ? '4,790' : '499' }}
                      </span>
                      <span class="text-gray-500 text-base sm:text-lg font-medium">
                        MXN/{{ isAnual ? 'Anual' : 'Mensual' }}
                      </span>
                    </div>
                  </div>
                  <div class="flex flex-col gap-4">
                    <div class="flex items-center gap-3">
                      <div class="w-5 h-5 flex items-center justify-center">
                        <div class="w-3.5 h-3.5 bg-rose-500 rounded-sm"></div>
                      </div>
                      <span class="text-gray-500 text-sm sm:text-base">Propiedades ilimitadas</span>
                    </div>
                    <div class="flex items-center gap-3">
                      <div class="w-5 h-5 flex items-center justify-center">
                        <div class="w-3.5 h-3.5 bg-rose-500 rounded-sm"></div>
                      </div>
                      <span class="text-gray-500 text-sm sm:text-base">Exposición automática en 6 portales</span>
                    </div>
                  </div>
                  <fwb-button
                    @click="() => goToStripe('planPremium')"
                    :disabled="!isActiveLocation"
                    class="w-full flex justify-center items-center px-5 py-2.5 bg-rose-500 hover:bg-rose-600 rounded-lg transition-colors duration-200 cursor-pointer"
                  >
                    <span class="text-white text-sm font-medium">Contratar Plan</span>
                  </fwb-button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <!-- Modal original (sin cambios) -->
      <div v-if="showModal" class="fade-in">
        <div v-show="!showPayments">
          <ModalFormNewPayPlanesComponent
            :address="address"
            :export_lat="property_lt"
            :export_ln="property_ln"
            @update-location="handleLocationUpdate"
          />
        </div>
        <div v-if="showPayments">
          <ModalFormPayPlanesComponent
            :address="address"
            :export_lat="property_lt"
            :export_ln="property_ln"
            @update-location="handleLocationUpdate"
          />
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import ToastAlert from "@/commond/ToastAlert.vue";
import { ref, onMounted, computed, watch } from 'vue';
import ModalFormNewPayPlanesComponent from "@/components/payments/FormPayNewPlanesComponente.vue";
import ModalFormPayPlanesComponent from "@/components/payments/FormPayPlanesComponent.vue";
import { useStore } from 'vuex';
import axios from 'axios';

// ─── Props ───────────────────────────────────────────────────────────────────
const props = defineProps({
  resp:       { type: Object, default: null },
  propertyId: { type: [String, Number], default: null },
  Type:       { type: String, default: 'agent' },
});

//const emit = defineEmits(['change-tab'])
// ─── Store / session ─────────────────────────────────────────────────────────
const store = useStore();
let session = computed(() => store.getters.getUserSession);

// ─── Estado general ───────────────────────────────────────────────────────────
const isAnual        = ref(false);
const toastAlert     = ref(false);
const typeMsg        = ref('');
const msg            = ref('');
const tittle         = ref('');
const plans          = ref([]);
const loader_profile = ref(false);
let showModal        = ref(false);
let showPayments     = ref(false);

// ─── Estado de la suscripción activa ─────────────────────────────────────────
// Estos valores se llenan al verificar si el asesor tiene una suscripción activa con tarjeta
const currentSubscription = ref({
  subscription_id:        null,  // ID local del sistema
  stripe_subscription_id: null,  // ID de Stripe (puede ser null)
  plan_id:                null,  // ID del plan actual
  plan_name:              '',
  plan_price:             '',
  billing_cycle:          '',
  payment_source:         '',    // 'card' | 'SPEI'
  status:                 '',    // 'active' | ...
  pending_price_id:       null,
});

// ─── Estado del cambio de plan ────────────────────────────────────────────────
const selectedPlanKey  = ref(null);  // key del plan seleccionado (ej: 'plan3_monthly')
const loadingChange    = ref(false);

// ─── Definición de planes disponibles ────────────────────────────────────────
// Cada entrada tiene: key único, label, precio display, properties label,
// precio numérico (para buscar en el array `plans` del backend),
// tipo de ciclo (monthly/annual)
const allPlans = computed(() => [
  { key: 'plan3_monthly',   label: '3 propiedades',       price: '149',   properties: '3 propiedades',           amount: 149,  type: 'monthly'  },
  { key: 'plan5_monthly',   label: '5 propiedades',       price: '199',   properties: '5 propiedades',           amount: 199,  type: 'monthly'  },
  { key: 'plan10_monthly',  label: '10 propiedades',      price: '399',   properties: '10 propiedades',          amount: 399,  type: 'monthly'  },
  { key: 'planPrem_monthly',label: 'Ilimitado',           price: '499',   properties: 'Propiedades ilimitadas',  amount: 499,  type: 'monthly'  },
  { key: 'plan3_annual',    label: '3 propiedades',       price: '1,430', properties: '3 propiedades',           amount: 1430, type: 'annual'   },
  { key: 'plan5_annual',    label: '5 propiedades',       price: '1,910', properties: '5 propiedades',           amount: 1910, type: 'annual'   },
  { key: 'plan10_annual',   label: '10 propiedades',      price: '3,830', properties: '10 propiedades',          amount: 3830, type: 'annual'   },
  { key: 'planPrem_annual', label: 'Ilimitado',           price: '4,790', properties: 'Propiedades ilimitadas',  amount: 4790, type: 'annual'   },
]);

// Planes filtrados según el toggle mensual/anual
const displayPlans = computed(() =>
  allPlans.value.filter(p => p.type === (isAnual.value ? 'annual' : 'monthly'))
);

// El plan seleccionado completo
const selectedPlanObj = computed(() =>
  allPlans.value.find(p => p.key === selectedPlanKey.value) || null
);

// Si el plan seleccionado es el mismo que el actual → no mostrar panel de confirmación
const isCurrentPlanSelected = computed(() => {
  if (!selectedPlanObj.value) return false;
  return selectedPlanObj.value.amount === parseFloat(String(currentSubscription.value.plan_price).replace(/,/g, ''));
});

// ─── ¿Tiene suscripción activa con tarjeta? ───────────────────────────────────
const hasActiveCardSubscription = computed(() =>
  currentSubscription.value.status === 'active' &&
  currentSubscription.value.payment_source !== 'SPEI' &&
  currentSubscription.value.subscription_id !== null
);

// Bloquear cambio si ya hubo uno este mes (pending_price_id con valor)
const planChangeBlocked = computed(() =>
  !!currentSubscription.value.pending_price_id
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const togglePeriod = () => { isAnual.value = !isAnual.value; };

// Determina si un plan de la grilla es el plan actual del asesor
function isCurrentPlan(plan) {
  const currentAmount = parseFloat(String(currentSubscription.value.plan_price).replace(/,/g, ''));
  return plan.amount === currentAmount;
}

// Al hacer clic en una card: si es el plan actual no hace nada
function selectPlan(plan) {
  if (isCurrentPlan(plan)) return;
    if (planChangeBlocked.value) return;
  selectedPlanKey.value = selectedPlanKey.value === plan.key ? null : plan.key;
}

// Cuando cambia el toggle mensual/anual → limpiar selección
watch(isAnual, () => { selectedPlanKey.value = null; });

// ─── Cargar suscripción activa ────────────────────────────────────────────────
async function loadCurrentSubscription() {
  try {
    loader_profile.value = true;
    const options = { headers: { Authorization: `Bearer ${session.value.token_api}` } };

    const response = await axios.get(
      `${process.env.VUE_APP_API_BASE_URL}/broker/getSubscription/${session.value.id}`,
      options
    );

    if (response.status === 200 && response.data.status === 'active') {
      const data = response.data.data;
      const source = props.resp?.info?.last_subscription?.source || 'card';

      // Solo interesa la suscripción activa con tarjeta (no SPEI)
      if (source !== 'SPEI' && data.plan_id !== 9) {
        const priceFormatted = new Intl.NumberFormat('es-MX', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(data.amount);

        currentSubscription.value = {
          subscription_id:        data.id,
          stripe_subscription_id: data.stripe_subscription_id || null,
          plan_id:                data.plan_id,
          plan_name:              data.plan?.name || 'Plan actual',
          plan_price:             priceFormatted,
          billing_cycle:          data.billing_cycle === 'monthly' ? 'mensual' : 'anual',
          payment_source:         source,
          status:                 'active',
          pending_price_id:       data.pending_price_id || null,
        };

        // Pre-seleccionar el toggle según el ciclo actual
        isAnual.value = data.billing_cycle === 'annual';
      }
    }
  } catch (error) {
    console.log('Sin suscripción activa, mostrando modo normal:', error);
    // Si falla, queda con valores por defecto → modo original
  } finally {
    loader_profile.value = false;
  }
}

// ─── Confirmar cambio de plan ─────────────────────────────────────────────────
async function confirmPlanChange() {
  if (!selectedPlanObj.value || loadingChange.value) return;

  loadingChange.value = true;
  try {
    const options = { headers: { Authorization: `Bearer ${session.value.token_api}` } };

    // Buscar el plan en el catálogo del backend para obtener plan_id y price_stripe_id
    const targetPlan = plans.value.find(p =>
      p.price === selectedPlanObj.value.amount &&
      p.type  === (isAnual.value ? 'annual' : 'monthly') &&
      p.status === 'active'
    );

    if (!targetPlan) {
      showToast('error', 'No se encontró el plan seleccionado. Intenta de nuevo.');
      return;
    }

    const payload = {
      subscription_id:        currentSubscription.value.subscription_id,
      stripe_subscription_id: currentSubscription.value.stripe_subscription_id || null,
      new_plan_id:             targetPlan.id,
      new_price_id:            targetPlan.price_stripe_id || null,
    };

    const response = await axios.post(
      `${process.env.VUE_APP_API_BASE_URL}/broker/subscription/update-plan`,
      payload,
      options
    );

    if (response.status === 200) {
      showToast(
        'success',
        currentSubscription.value.stripe_subscription_id
          ? 'Plan actualizado. El nuevo precio se aplicará en el siguiente ciclo de facturación.'
          : 'Plan actualizado correctamente.'
      );
      selectedPlanKey.value = null;
      // Recargar la suscripción para reflejar el cambio
      await loadCurrentSubscription();
    }
  } catch (error) {
    const resp = error.response;
    if (resp?.status === 401) {
      store.dispatch('clearUserSession');
    } else {
      showToast('error', resp?.data?.message || 'Error al actualizar el plan. Intenta de nuevo.');
    }
  } finally {
    loadingChange.value = false;
  }
}

// ─── Modo original: goToStripe ────────────────────────────────────────────────
const isTestEnvironment = () => {
  const apiUrl = process.env.VUE_APP_API_BASE_URL || '';
  return apiUrl.includes('localhost') || apiUrl.includes('qa.tuin.ai');
};

function getProfile() {
  showPayments.value = !showPayments.value;
}

function goToStripe(planKey) {
  if (planKey === 'planPremium' && isAnual.value) {
    const testLink = 'https://buy.stripe.com/test_7sYbJ28x5gHp3Ar14j3sI00';
    const prodLink = 'https://buy.stripe.com/9B614o3cLgHpgndfZd3sI0n';
    window.open(isTestEnvironment() ? testLink : prodLink, '_blank');
    return;
  }

  const planMapping = {
    plan3:      { price: isAnual.value ? 1430 : 149,  type: isAnual.value ? 'annual' : 'monthly' },
    plan5:      { price: isAnual.value ? 1910 : 199,  type: isAnual.value ? 'annual' : 'monthly' },
    plan10:     { price: isAnual.value ? 3830 : 399,  type: isAnual.value ? 'annual' : 'monthly' },
    planPremium:{ price: isAnual.value ? 4790 : 499,  type: isAnual.value ? 'annual' : 'monthly' },
  };

  const planInfo    = planMapping[planKey];
  const selectedPlan = plans.value.find(
    p => p.price === planInfo.price && p.type === planInfo.type && p.status === 'active'
  );

  if (selectedPlan?.payment_link) {
    window.open(selectedPlan.payment_link, '_blank');
  } else {
    console.warn('⚠️ No se encontró link de pago para:', planKey, planInfo);
  }
}

// ─── Cargar catálogo de planes ────────────────────────────────────────────────
async function fetchPlanes() {
  try {
    const options = { headers: { Authorization: `Bearer ${session.value.token_api}` } };
    const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/planes-all`, options);

    if (response.status === 200 && response.data?.plans) {
      plans.value = response.data.plans;
    }
  } catch (error) {
    console.error('Error al obtener los planes:', error);
  }
}

// ─── Toast ────────────────────────────────────────────────────────────────────
function closeToast() {
  toastAlert.value = false;
}

function showToast(type, message, title = '') {
  toastAlert.value = true;
  typeMsg.value    = type;
  msg.value        = message;
  tittle.value     = title;
  setTimeout(() => { toastAlert.value = false; }, 3500);
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────
onMounted(async () => {
  try {
    loader_profile.value = true;
    await fetchPlanes();
    await loadCurrentSubscription();
    getProfile();
  } catch (error) {
    console.error('Error al inicializar FormPlanesComponent:', error);
  }
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
</style>